/**
 * Grain LP backdrop.
 *
 * A Three.js plane running a four-colour "lava lamp" gradient behind the
 * concept collage. Photo frames each tint a local well so the wash runs
 * warm around the leather/red-wall shots and green around the door, rather
 * than shifting the whole viewport at once.
 *
 * Three.js is only fetched once the section approaches the viewport, and
 * grain-bg.jpg stays painted underneath the canvas, so the page never waits on
 * WebGL and degrades to the comp artwork when it is unavailable.
 */
(function () {
  'use strict';

  var VERTEX_SHADER = [
    'varying vec2 vTextureCoord;',
    'void main() {',
    '  vTextureCoord = uv;',
    '  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
    '}'
  ].join('\n');

  var FRAGMENT_SHADER = [
    'precision highp float;',
    '',
    'uniform float uTime;',
    'uniform float uScroll;',
    'uniform vec2 uResolution;',
    'uniform vec3 uTopColor;',
    'uniform vec3 uBottomColor;',
    'uniform vec3 uAccentColor;',
    'uniform vec3 uDarkColor;',
    'uniform vec3 uMixA;',
    'uniform vec3 uMixB;',
    'uniform vec2 uFocusPoint;',
    'uniform float uFocusStrength;',
    'uniform float uGrain;',
    'uniform vec2 uWellPos[8];',
    'uniform vec3 uWellColor[8];',
    'uniform float uWellRadius[8];',
    'uniform float uWellStrength[8];',
    'uniform float uWellSide[8];',
    'uniform float uStoryDepth;',
    'uniform float uMobile;',
    '',
    'varying vec2 vTextureCoord;',
    '',
    'mat2 Rot(float a) {',
    '  float s = sin(a);',
    '  float c = cos(a);',
    '  return mat2(c, -s, s, c);',
    '}',
    '',
    'vec2 hash(vec2 p) {',
    '  p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));',
    '  return fract(sin(p) * 43758.5453);',
    '}',
    '',
    'float noise(in vec2 p) {',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),',
    '                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),',
    '                mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),',
    '                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);',
    '  return 0.5 + 0.5 * n;',
    '}',
    '',
    'float filmGrainNoise(in vec2 uv) {',
    '  return length(hash(vec2(uv.x, uv.y)));',
    '}',
    '',
    'void main() {',
    '  vec2 uv = vTextureCoord;',
    '  float aspectRatio = uResolution.x / uResolution.y;',
    '',
    '  vec2 tuv = uv - 0.5;',
    '  float t = uTime * 0.9;',
    '',
    '  float degree = noise(vec2(t * 0.12, tuv.x * tuv.y));',
    '  tuv.y *= 1.0 / aspectRatio;',
    '  tuv *= Rot(radians((degree - 0.5) * 280.0 + 180.0));',
    '  tuv.y *= aspectRatio;',
    '',
    '  float frequency = 3.0;',
    '  float amplitude = 14.0;',
    '  float speed = t * 1.6;',
    '  tuv.x += sin(tuv.y * frequency + speed) / amplitude;',
    '  tuv.y += sin(tuv.x * frequency * 1.5 + speed) / (amplitude * 0.5);',
    '',
    '  vec3 warm = mix(uBottomColor, uMixA, 0.44);',
    '  vec3 cool = mix(uDarkColor, uMixB, 0.40);',
    '  vec3 mid = uBottomColor;',
    '  vec3 layer1 = mix(warm, mid, smoothstep(-0.45, 0.35, (tuv * Rot(radians(-5.0))).x));',
    '  vec3 layer2 = mix(mid, cool, smoothstep(-0.45, 0.35, (tuv * Rot(radians(-5.0))).x));',
    '  vec3 flatColor = mix(layer1, layer2, smoothstep(0.5, -0.25, tuv.y));',
    '',
    '  vec2 warp = tuv - (uv - 0.5);',
    '  vec2 focusPt = uFocusPoint - 0.5;',
    '  vec2 delta = (uv - 0.5) - focusPt + warp * 0.5;',
    '  delta.x *= aspectRatio;',
    '  float d = length(delta);',
    '',
    '  float radialMask = smoothstep(0.6, 0.0, d) * uFocusStrength;',
    '  vec3 col = mix(flatColor, mix(flatColor, uTopColor, 0.45), radialMask);',
    '',
    '  float depth = clamp(uStoryDepth, 0.0, 1.0);',
    '  float redZone = smoothstep(0.18, 0.36, depth) * (1.0 - smoothstep(0.42, 0.62, depth));',
    '  float greenZone = smoothstep(0.44, 0.72, depth);',
    '  float leftGlow = mix(0.42, 1.0, smoothstep(0.95, 0.0, uv.x));',
    '  vec3 redWash = mix(uMixA, vec3(0.27, 0.17, 0.13), 0.40);',
    '  vec3 greenWash = mix(uAccentColor, vec3(0.13, 0.18, 0.16), 0.28);',
    '  col = mix(col, mix(col, redWash, mix(0.24, 0.38, uMobile)), redZone * leftGlow);',
    '  col = mix(col, mix(col, greenWash, mix(0.26, 0.40, uMobile)), greenZone);',
    '',
    '  for (int i = 0; i < 8; i++) {',
    '    float strength = uWellStrength[i];',
    '    vec2 deltaWell = (uv - uWellPos[i]) + warp * 0.22;',
    '    deltaWell.x *= aspectRatio;',
    '    float side = uWellSide[i];',
    '    deltaWell.x *= mix(1.0, 1.25, step(0.5, -side));',
    '    float radius = max(uWellRadius[i], 0.001);',
    '    float falloff = smoothstep(radius, radius * 0.06, length(deltaWell)) * strength;',
    '    float leftMaskPc = mix(0.28, 1.0, smoothstep(0.78, 0.08, uv.x));',
    '    float leftMaskSp = mix(0.55, 1.0, smoothstep(1.05, 0.0, uv.x));',
    '    float leftMask = mix(leftMaskPc, leftMaskSp, uMobile);',
    '    falloff *= mix(1.0, leftMask, step(0.5, -side));',
    '    float wellMix = mix(0.64, 0.56, uMobile);',
    '    col = mix(col, mix(col, uWellColor[i], wellMix), falloff);',
    '  }',
    '',
    '  col = col - filmGrainNoise(uv) * uGrain;',
    '',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  /* Rendering a soft gradient at full device resolution buys nothing, and the
   * film grain reads better slightly coarse. */
  var QUALITY = 0.7;
  var MAX_PIXEL_RATIO = 2;
  var WELL_COUNT = 8;

  function hexToRgb(hex) {
    var value = String(hex || '').replace('#', '').trim();
    if (value.length === 3) {
      value = value[0] + value[0] + value[1] + value[1] + value[2] + value[2];
    }
    if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
    return [
      parseInt(value.slice(0, 2), 16) / 255,
      parseInt(value.slice(2, 4), 16) / 255,
      parseInt(value.slice(4, 6), 16) / 255
    ];
  }

  function supportsWebGL() {
    try {
      var probe = document.createElement('canvas');
      return !!(probe.getContext('webgl') || probe.getContext('experimental-webgl'));
    } catch (error) {
      return false;
    }
  }

  function createBackdrop(root) {
    var canvas = root.querySelector('canvas');
    var section = root.closest('.GrainStory');
    if (!canvas || !section || !supportsWebGL()) return null;

    var colors = {
      top: hexToRgb(root.getAttribute('data-top-color')) || [0.431, 0.533, 0.576],
      bottom: hexToRgb(root.getAttribute('data-bottom-color')) || [0.333, 0.357, 0.349],
      accent: hexToRgb(root.getAttribute('data-accent-color')) || [0.094, 0.196, 0.157],
      dark: hexToRgb(root.getAttribute('data-dark-color')) || [0.145, 0.157, 0.145],
      mixA: hexToRgb(root.getAttribute('data-mix-a')) || [0.216, 0.161, 0.137],
      mixB: hexToRgb(root.getAttribute('data-mix-b')) || [0.318, 0.384, 0.420]
    };

    var photos = section.querySelectorAll('.GrainStory__Photo');
    var wellColors = [];
    var wellPositions = [];
    var wellRadii = [];
    var wellStrengths = [];
    var wellSides = [];
    for (var w = 0; w < WELL_COUNT; w++) {
      wellPositions.push(null);
      wellRadii.push(0.5);
      wellStrengths.push(0);
      var photo = photos[w];
      var wellHex = photo && photo.getAttribute('data-well-color');
      var rgb = hexToRgb(wellHex) || colors.bottom.slice();
      if (rgb[0] > rgb[1]) {
        wellColors.push([
          Math.min(1, rgb[0] * 1.04 + 0.02),
          Math.min(1, rgb[1] * 1.06 + 0.02),
          rgb[2] * 0.96
        ]);
      } else if (rgb[1] > rgb[0] + 0.02) {
        wellColors.push([
          rgb[0] * 0.96,
          Math.min(1, rgb[1] * 1.08 + 0.02),
          Math.min(1, rgb[2] * 1.02)
        ]);
      } else {
        wellColors.push(rgb);
      }
      var bias = photo && photo.getAttribute('data-well-bias');
      wellSides.push(bias === 'left' ? -1 : bias === 'right' ? 1 : 0);
    }

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var coarsePointer = window.matchMedia('(pointer: coarse)').matches;

    var three = null;
    var renderer = null;
    var scene = null;
    var camera = null;
    var mesh = null;
    var uniforms = null;
    var sizeWatcher = null;
    var running = false;
    var visible = false;
    var destroyed = false;

    /* Accumulated rather than read off a clock, so scrolling the section out of
     * view and back does not rewind the animation. */
    var elapsed = 0;
    var lastFrame = 0;

    var focus = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5, strength: 0, target: 0 };

    function resize() {
      if (!renderer) return;
      var width = root.clientWidth || window.innerWidth;
      var height = root.clientHeight || window.innerHeight;
      if (!width || !height) return;
      renderer.setSize(width, height, false);
      uniforms.uResolution.value.set(width, height);
    }

    function pin() {
      if (destroyed || !renderer) return;
      var viewport = window.visualViewport;
      var vh = (viewport && viewport.height) || window.innerHeight || 1;
      var rect = section.getBoundingClientRect();
      var hero = document.querySelector('.GrainHero');
      var heroBottom = hero ? Math.max(0, hero.getBoundingClientRect().bottom) : 0;
      var intersecting = rect.bottom > 0 && rect.top < vh;
      var topClip = intersecting ? Math.max(0, rect.top, heroBottom) : 0;
      var bottomClip = intersecting ? Math.max(0, vh - rect.bottom) : vh;
      root.style.clipPath = 'inset(' + topClip + 'px 0 ' + bottomClip + 'px 0)';
      root.style.webkitClipPath = 'inset(' + topClip + 'px 0 ' + bottomClip + 'px 0)';
      if (uniforms) {
        var span = rect.height + vh;
        var progress = span > 0 ? (vh - rect.top) / span : 0;
        uniforms.uScroll.value = Math.max(0, Math.min(1, progress));
        uniforms.uStoryDepth.value = Math.max(0, Math.min(1, (vh * 0.42 - rect.top) / Math.max(rect.height, 1)));

        var canvasRect = canvas.getBoundingClientRect();
        var cw = canvasRect.width || 1;
        var ch = canvasRect.height || 1;
        var portrait = ch > cw * 1.05;
        uniforms.uMobile.value = portrait ? 1 : 0;
        for (var i = 0; i < WELL_COUNT; i++) {
          var photo = photos[i];
          if (!photo) {
            uniforms.uWellStrength.value[i] = 0;
            continue;
          }
          var box = photo.getBoundingClientRect();
          var bias = photo.getAttribute('data-well-bias') || 'center';
          var scale = parseFloat(photo.getAttribute('data-well-scale') || '1') || 1;
          if (portrait) scale *= 1.25;
          if (portrait && (i === 2 || i === 5 || i === 6)) scale *= 1.15;
          var anchorX = bias === 'left' ? 0.22 : bias === 'right' ? 0.58 : 0.5;
          var cx = box.left + box.width * anchorX;
          var cy = box.top + box.height * 0.5;
          uniforms.uWellPos.value[i].set(
            (cx - canvasRect.left) / cw,
            1 - (cy - canvasRect.top) / ch
          );
          var overlap = Math.max(0, Math.min(box.bottom, canvasRect.bottom) - Math.max(box.top, canvasRect.top));
          var onScreen = box.height > 0 ? overlap / box.height : 0;
          var entered = Math.max(0, (onScreen - 0.04) / 0.55);
          uniforms.uWellStrength.value[i] = Math.min(1, entered);
          var radiusMul = portrait ? 1.45 : 0.72;
          var radiusPx = Math.sqrt(box.width * box.width + box.height * box.height) * radiusMul;
          uniforms.uWellRadius.value[i] = Math.max(0.1, radiusPx / ch) * scale;
          uniforms.uWellSide.value[i] = wellSides[i];
        }
        uniforms.uWellStrength.needsUpdate = true;
        uniforms.uWellRadius.needsUpdate = true;
      }
      resize();
    }

    function frame() {
      if (!running || destroyed) return;

      var now = performance.now();
      elapsed += Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;
      uniforms.uTime.value = elapsed;

      focus.x += (focus.targetX - focus.x) * 0.06;
      focus.y += (focus.targetY - focus.y) * 0.06;
      focus.strength += (focus.target - focus.strength) * 0.05;
      uniforms.uFocusPoint.value.set(focus.x, focus.y);
      uniforms.uFocusStrength.value = focus.strength;

      renderer.render(scene, camera);
    }

    function start() {
      if (destroyed || running || !renderer || reduceMotion) return;
      running = true;
      lastFrame = performance.now();
      renderer.setAnimationLoop(frame);
    }

    function stop() {
      if (!running || !renderer) return;
      running = false;
      renderer.setAnimationLoop(null);
    }

    function sync() {
      if (visible && !document.hidden) start();
      else stop();
    }

    function onPointerMove(event) {
      if (coarsePointer || !visible) return;
      var rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      focus.targetX = (event.clientX - rect.left) / rect.width;
      focus.targetY = 1 - (event.clientY - rect.top) / rect.height;
      focus.target = 1;
    }

    function onPointerLeave() {
      focus.target = 0;
    }

    function onVisibilityChange() {
      sync();
    }

    function onContextLost(event) {
      event.preventDefault();
      stop();
      section.classList.remove('GrainStory--backdrop-ready');
    }

    function build(module) {
      if (destroyed) return;
      three = module;

      uniforms = {
        uTime: { value: 0 },
        uScroll: { value: 0 },
        uResolution: { value: new three.Vector2(1, 1) },
        uTopColor: { value: new three.Vector3().fromArray(colors.top) },
        uBottomColor: { value: new three.Vector3().fromArray(colors.bottom) },
        uAccentColor: { value: new three.Vector3().fromArray(colors.accent) },
        uDarkColor: { value: new three.Vector3().fromArray(colors.dark) },
        uMixA: { value: new three.Vector3().fromArray(colors.mixA) },
        uMixB: { value: new three.Vector3().fromArray(colors.mixB) },
        uFocusPoint: { value: new three.Vector2(0.5, 0.5) },
        uFocusStrength: { value: 0 },
        uGrain: { value: 0.06 },
        uWellPos: { value: wellPositions.map(function () { return new three.Vector2(0.5, 0.5); }) },
        uWellColor: { value: wellColors.map(function (rgb) { return new three.Vector3().fromArray(rgb); }) },
        uWellRadius: { value: wellRadii.slice() },
        uWellStrength: { value: wellStrengths.slice() },
        uWellSide: { value: wellSides.slice() },
        uStoryDepth: { value: 0 },
        uMobile: { value: 0 }
      };

      renderer = new three.WebGLRenderer({ canvas: canvas, antialias: false, alpha: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO) * QUALITY);

      scene = new three.Scene();
      camera = new three.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      mesh = new three.Mesh(
        new three.PlaneGeometry(2, 2),
        new three.ShaderMaterial({
          vertexShader: VERTEX_SHADER,
          fragmentShader: FRAGMENT_SHADER,
          uniforms: uniforms,
          depthTest: false,
          depthWrite: false
        })
      );
      scene.add(mesh);

      canvas.addEventListener('webglcontextlost', onContextLost);

      pin();
      renderer.render(scene, camera);
      section.classList.add('GrainStory--backdrop-ready');

      if (window.ResizeObserver) {
        sizeWatcher = new ResizeObserver(function () {
          pin();
        });
        sizeWatcher.observe(section);
      }
      window.addEventListener('resize', pin);
      window.addEventListener('scroll', pin, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', pin);
        window.visualViewport.addEventListener('scroll', pin, { passive: true });
      }

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      section.addEventListener('pointerleave', onPointerLeave);

      sync();
    }

    function loadThree(src) {
      if (window.__GRAIN_THREE) return Promise.resolve(window.__GRAIN_THREE);
      return new Promise(function (resolve, reject) {
        var settled = false;
        function finish(mod) {
          if (settled || !mod) return;
          settled = true;
          window.__GRAIN_THREE = mod;
          resolve(mod);
        }
        document.addEventListener('grain:three-ready', function () {
          finish(window.__GRAIN_THREE);
        }, { once: true });
        if (src) {
          import(src).then(finish).catch(function () {
            /* The module tag in the section may still resolve. */
          });
        }
        setTimeout(function () {
          if (!settled) reject(new Error('Three.js timeout'));
        }, 8000);
      });
    }

    var loading = false;
    function load() {
      if (loading || destroyed) return;
      loading = true;
      loadThree(root.getAttribute('data-three-src')).then(build).catch(function () {
        /* Leave grain-bg.jpg showing. */
      });
    }

    /* Start fetching a screen ahead so the fade-in lands before the section
     * reaches the viewport. */
    var loadWatcher = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        loadWatcher.disconnect();
        load();
      },
      { rootMargin: '100% 0px' }
    );
    loadWatcher.observe(section);

    var playWatcher = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      pin();
      sync();
    });
    playWatcher.observe(section);

    load();

    return function destroy() {
      destroyed = true;
      stop();
      loadWatcher.disconnect();
      playWatcher.disconnect();
      if (sizeWatcher) sizeWatcher.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('resize', pin);
      window.removeEventListener('scroll', pin);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', pin);
        window.visualViewport.removeEventListener('scroll', pin);
      }
      window.removeEventListener('resize', resize);
      section.removeEventListener('pointerleave', onPointerLeave);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      if (renderer) renderer.dispose();
    };
  }

  var instances = new WeakMap();

  function init(scope) {
    (scope || document).querySelectorAll('[data-grain-backdrop]').forEach(function (root) {
      if (instances.has(root)) return;
      var destroy = createBackdrop(root);
      if (destroy) instances.set(root, destroy);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-grain-backdrop]').forEach(function (root) {
      var destroy = instances.get(root);
      if (destroy) {
        destroy();
        instances.delete(root);
      }
    });
  });
})();
